import { ReaderError } from '@/model/DeviceAdminModel';
import cx from 'classnames';
import React, { useContext, useMemo } from 'react';
import { batch } from 'react-redux';
import { SelectOptions } from '../../../../../../../model/CommonModel';
import { Select } from '../../../../../../common';
import { DoorStoreSingleContext } from '../../../../DoorModal/DoorStoreSingleContext';
import { StoreContext, setAssuranceLevel, setEntryReaderError, setRS485ReaderType, setReaderErrorType } from '../../../contextReader';
import styles from '../../General/general.module.scss';

const readerTypeSelectId: string = 'readerModalReaderTypeSelect';

type Props = {
	useDoorContext: boolean;
};

const ReaderType: React.FC<Props> = ({ useDoorContext }) => {
	//#region state management variables
	if (useDoorContext) {
		var {
			contextStateDoor: {
				entryReader: {
					rS485ReaderTypes,
					isDoorWireless,
					selections: { rS485ReaderType },
					readerErrorType,
					readerErrorMessage,
					isEntryReaderError,
				},
			},
			dispatcherDoor,
		} = useContext(DoorStoreSingleContext);
	} else {
		var {
			readerState: {
				rS485ReaderTypes,
				isDoorWireless,
				selections: { rS485ReaderType },
				readerErrorType,
				readerErrorMessage,
				isEntryReaderError,
			},
			dispatcher,
		} = useContext(StoreContext);
	}

	const rs485Types: SelectOptions<string>[] = useMemo(() => {
		let options: SelectOptions<string>[] = rS485ReaderTypes.map(x => ({ label: x.Name, value: x.Id.toString(), id: x.Id.toString() }));
		const idx = rS485ReaderTypes.findIndex(rst => rst.Id === rS485ReaderType);
		if (idx < 0) {
			options = [{ label: ' ', value: '-1', id: '-1' }, ...options];
		}
		return options;
	}, [rS485ReaderType, rS485ReaderTypes]);

	const readerType = useMemo(() => {
		if (!isDoorWireless) {
			return rS485ReaderType.toString();
		} else if (rS485ReaderTypes.length > 0) {
			return rS485ReaderTypes[0].Id.toString();
		}

		return undefined;
	}, [rS485ReaderType]);

	const errorMessage = useMemo(() => {
		if (readerErrorType === ReaderError.RS485ReaderType) {
			return <span className={styles.errorMessage}>{readerErrorMessage}</span>;
		}
		return undefined;
	}, [readerErrorType]);
	//#endregion

	const handleOnSetReaderType = (value: string) => {
		batch(() => {
			if (!isDoorWireless) {
				useDoorContext ? dispatcherDoor(setAssuranceLevel(0)) : dispatcher(setAssuranceLevel(0));
			}
			useDoorContext ? dispatcherDoor(setRS485ReaderType(Number(value))) : dispatcher(setRS485ReaderType(Number(value)));

			if (
				readerErrorType === ReaderError.RS485ReaderType ||
				readerErrorType === ReaderError.Assurance ||
				readerErrorType === ReaderError.DefaultAssuranceLevel
			) {
				useDoorContext
					? (dispatcherDoor(setReaderErrorType(undefined)), dispatcherDoor(setEntryReaderError(false)))
					: dispatcher(setReaderErrorType(undefined));
			}
		});
	};

	return (
		<div className={styles.readerType}>
			<label htmlFor={readerTypeSelectId}>{_('ReaderType')}</label>
			<div>
				<Select
					className={cx(styles.select, {
						[styles.error]: readerErrorType === ReaderError.RS485ReaderType && (isEntryReaderError || !useDoorContext),
					})}
					options={rs485Types}
					onChange={handleOnSetReaderType}
					id={readerTypeSelectId}
					key='readerType'
					value={readerType}
					getPopupContainer={() => document.getElementById('generalReaderContainer')}
				/>
				{readerErrorType === ReaderError.RS485ReaderType && (isEntryReaderError || !useDoorContext) && errorMessage}
			</div>
		</div>
	);
};

export { ReaderType };
